import { isEmpty, get } from "lodash";
import { produce } from 'immer';
import jwt_decode from "jwt-decode";

// Helper function to check if a filter value is meaningful
const hasFilterValue = (value: any): boolean => {
    return value !== undefined &&
           value !== null &&
           value !== '' &&
           !(Array.isArray(value) && value.length === 0) &&
           !(typeof value === 'object' && isEmpty(value));
};

export const applyFilterToData = (data: any[], filterProp: string, filterValues: any, filterConfig: any[]) => {
    const filterValueExists = hasFilterValue(filterValues);

    if (!filterValueExists || !data || !Array.isArray(data)) {
        return data;
    }

    const currentFilterConfig = filterConfig?.find(
        (item: any) => item.name === filterProp
    );

    const beforeCount = data.length;

    const filteredData = data.filter((row: any) => {
        if (currentFilterConfig?.comparator) {
            // Pass the filter config to the comparator function
            return currentFilterConfig.comparator(filterProp, filterValues, row, currentFilterConfig);
        } else {
            const cellValue = get(row, filterProp);

            if (Array.isArray(filterValues)) {
                // Check if this filter should use AND logic (default is now OR)
                const useAndLogic = currentFilterConfig?.useAndLogic || false;

                if (Array.isArray(cellValue)) {
                    if (useAndLogic) {
                        // AND: Cell must contain ALL filter values
                        return filterValues.every(filterVal => cellValue.includes(filterVal));
                    } else {
                        // OR (default): Cell must contain ANY filter value
                        return filterValues.some(filterVal => cellValue.includes(filterVal));
                    }
                } else {
                    if (useAndLogic) {
                        // AND: Cell string must contain ALL filter values
                        const cellString = String(cellValue).toLowerCase();
                        return filterValues.every(filterVal =>
                            cellString.includes(String(filterVal).toLowerCase())
                        );
                    } else {
                        // OR (default): Cell value must match ANY filter value
                        return filterValues.includes(cellValue);
                    }
                }
            }

            // Default string matching for text filters
            return String(cellValue).toLowerCase().includes(String(filterValues).toLowerCase());
        }
    });

    return filteredData;
};

export const getIncrementalFilteredData = (originalData: any[], filters: any, filterConfig: any[]) => {
    if (!originalData || !Array.isArray(originalData) || isEmpty(filters)) {
        return originalData;
    }

    let currentData = [...originalData];

    Object.entries(filters).forEach(([filterProp, filterValues]: any) => {
        // Only apply filter if it has a meaningful value
        if (hasFilterValue(filterValues)) {
            currentData = applyFilterToData(currentData, filterProp, filterValues, filterConfig);
        }
    });

    return currentData;
};

export const getFilteredTableData = (tableProps: any, filters: any, tableInstance: any) => {
    const data = tableProps?.tableComponentData;

    if (!data || !Array.isArray(data)) {
        console.warn("Table component data is not an array:", data);
        return [];
    }

    return getIncrementalFilteredData(
        data,
        filters,
        tableProps?.tableComponentFilterBar?.filterConfig
    );
};

// Utility function for extracting specified fields
export const extractFieldsWithConfig = (state: any, config: any) => {
    return produce(state, (draft: any) => {
        for (const [key, fields] of Object.entries(config)) {
            if (state[key] !== undefined) {
                draft[key] = (fields as any[]).reduce((acc: any, field: any) => {
                    if (field in state[key]) {
                        acc[field] = state[key][field];
                    }
                    return acc;
                }, {});
            }
        }
    });
};

export const getUserName = () => {
    const idToken = localStorage.getItem('idToken');
    if (idToken) {
        const decodedToken = jwt_decode(idToken) as any;
        return decodedToken?.['email'] || '';
    }
    return '';
};
